import React from "react";

/**
 * Detects whether an error is the typical "stale-chunk-after-deploy"
 * failure (webpack code-split + new build = different hashed filenames).
 * Symptoms:
 *   • `error.name === 'ChunkLoadError'`
 *   • `error.message` mentions "Loading chunk" or "Loading CSS chunk"
 *   • Or it's a network error while fetching `*.chunk.js` / `*.chunk.css`
 */
function isChunkLoadError(error) {
  if (!error) return false;
  if (error.name === "ChunkLoadError") return true;
  const msg = String(error.message || error || "");
  return (
    /Loading chunk [^\s]+ failed/i.test(msg) ||
    /Loading CSS chunk [^\s]+ failed/i.test(msg) ||
    /ChunkLoadError/i.test(msg) ||
    (/Failed to fetch dynamically imported module/i.test(msg) &&
      /\.chunk\.(js|css)/i.test(msg))
  );
}

/**
 * Hard reload that bypasses bfcache and any stale code in memory.
 * `?_=<timestamp>` defeats the "smart" CDN/service-worker cache.
 */
function hardReload() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("_", Date.now().toString());
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}

export default function SentryErrorFallback({ error, resetError }) {
  const isChunk = isChunkLoadError(error);

  // For chunk-load errors the React state reset is useless — the SPA is
  // still holding a reference to a JS file that no longer exists on the
  // CDN. Replace the action with a true reload.
  const handleRetry = () => {
    if (isChunk) {
      hardReload();
    } else {
      resetError?.();
    }
  };

  // After a deploy we usually want the page to come back transparently
  // for the user. If we land here with a chunk error AND we haven't
  // already attempted an automatic reload this session, kick one off
  // after a short delay so they don't have to tap the button at all.
  React.useEffect(() => {
    if (!isChunk) return;
    const key = "mesp-chunk-reload-attempted";
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, String(Date.now()));
    const t = setTimeout(hardReload, 800);
    return () => clearTimeout(t);
  }, [isChunk]);

  return (
    <div
      data-testid="sentry-error-fallback"
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: "#0B1739", color: "#F8FAFC" }}
    >
      <div
        className="max-w-md w-full rounded-2xl p-8 text-center"
        style={{
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(202,162,84,0.35)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
        }}
      >
        <div
          className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full"
          style={{ background: "rgba(202,162,84,0.15)", color: "#CAA254" }}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold mb-2">
          {isChunk ? "تم تحديث المنصة" : "حدث خطأ غير متوقع"}
        </h1>
        <p className="text-sm opacity-80 mb-2">
          {isChunk
            ? "نزّلنا تحديثاً جديداً للمنصة. سيتم تحديث الصفحة تلقائياً لتعمل بآخر إصدار. لن تفقد أي تقدّم محفوظ."
            : "نأسف للإزعاج — تم إبلاغ الفريق التقني تلقائياً وسيتم العمل على الإصلاح."}
        </p>
        {error?.message && !isChunk ? (
          <p className="text-xs opacity-60 mb-6 break-words">{String(error.message)}</p>
        ) : null}
        <button
          data-testid="sentry-error-retry-button"
          onClick={handleRetry}
          className="mt-3 px-6 py-2.5 rounded-lg font-semibold transition"
          style={{ background: "#CAA254", color: "#0B1739" }}
        >
          {isChunk ? "تحديث الآن" : "إعادة المحاولة"}
        </button>
      </div>
    </div>
  );
}
